// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { Plus, Trash2 } from "lucide-react";
import { useCallback } from "react";
import { toast } from "sonner";
import { FilterRow } from "@/components/filter-builder/filter-row";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AvailableFilterField } from "@/hooks/use-available-filter-fields";
import {
  type FilterGroup,
  type FilterOperator,
  isFilterGroup,
  type StructuredFilter,
} from "@/lib/filters";

// Maximum nesting depth for filter groups to prevent performance issues
const MAX_DEPTH = 5;

export interface RecursiveFilterBuilderProps {
  availableFields: AvailableFilterField[];
  value: FilterGroup;
  onChange: (group: FilterGroup) => void;
  className?: string;
  depth?: number;
  /**
   * Optional function to filter available fields based on the current group's context.
   * When provided, each nested group will call this function with its FilterGroup
   * to get the fields available for that specific group.
   * This enables per-group field filtering based on record_type or other criteria.
   */
  getFieldsForGroup?: (group: FilterGroup) => AvailableFilterField[];
}

export function RecursiveFilterBuilder({
  availableFields,
  value,
  onChange,
  className,
  depth = 0,
  getFieldsForGroup,
}: RecursiveFilterBuilderProps) {
  // If getFieldsForGroup is provided, use it to filter fields for this group
  // Otherwise, use the full availableFields list
  const fieldsForThisGroup = getFieldsForGroup
    ? getFieldsForGroup(value)
    : availableFields;
  const handleOperatorChange = useCallback(
    (newOperator: "AND" | "OR") => {
      onChange({ ...value, operator: newOperator });
    },
    [value, onChange],
  );

  const handleFilterChange = useCallback(
    (index: number, updatedFilter: StructuredFilter) => {
      const newFilters = [...value.filters];
      newFilters[index] = updatedFilter;
      onChange({ ...value, filters: newFilters });
    },
    [value, onChange],
  );

  const handleGroupChange = useCallback(
    (index: number, updatedGroup: FilterGroup) => {
      const newFilters = [...value.filters];
      newFilters[index] = updatedGroup;
      onChange({ ...value, filters: newFilters });
    },
    [value, onChange],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const newFilters = value.filters.filter((_, i) => i !== index);
      onChange({ ...value, filters: newFilters });
    },
    [value, onChange],
  );

  const handleAddFilter = useCallback(() => {
    let defaultOp: FilterOperator | null = null;

    if (fieldsForThisGroup.length > 0) {
      const firstField = fieldsForThisGroup[0];
      if (firstField.validOperators && firstField.validOperators.length > 0) {
        defaultOp = firstField.validOperators[0];
      }
    }

    const newFilter: StructuredFilter = {
      field: null,
      op: defaultOp,
      values: null,
    };

    onChange({ ...value, filters: [...value.filters, newFilter] });
  }, [fieldsForThisGroup, value, onChange]);

  const handleAddGroup = useCallback(() => {
    if (depth >= MAX_DEPTH) {
      toast.error(
        `Maximum nesting depth reached (${MAX_DEPTH} levels). Cannot add more nested groups.`,
      );
      return;
    }

    const newGroup: FilterGroup = {
      operator: "AND",
      filters: [],
    };

    onChange({ ...value, filters: [...value.filters, newGroup] });
  }, [value, onChange, depth]);

  const isNested = depth > 0;
  const containerClass = isNested
    ? "border-l-2 border-muted pl-4 ml-2"
    : className;

  return (
    <div className={containerClass}>
      {/* Operator selector */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-medium">Match</span>
        <Select value={value.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger className="w-24 h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AND">ALL</SelectItem>
            <SelectItem value="OR">ANY</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">of the following:</span>
      </div>

      {/* Filters and nested groups */}
      <div className="space-y-2">
        {value.filters.map((filter, index) => {
          const isGroup = isFilterGroup(filter);

          return (
            <div key={index}>
              {isGroup ? (
                <Card className="p-3 bg-muted/30">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <RecursiveFilterBuilder
                        availableFields={availableFields}
                        value={filter}
                        onChange={(updated) =>
                          handleGroupChange(index, updated)
                        }
                        depth={depth + 1}
                        getFieldsForGroup={getFieldsForGroup}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(index)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </Card>
              ) : (
                <FilterRow
                  filter={filter}
                  availableFields={fieldsForThisGroup}
                  onChange={(updated) => handleFilterChange(index, updated)}
                  onDelete={() => handleDelete(index)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Add buttons */}
      <div className="flex gap-2 mt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddFilter}
          disabled={fieldsForThisGroup.length === 0}
        >
          <Plus className="h-4 w-4 mr-1" /> Add filter
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddGroup}
          disabled={depth >= MAX_DEPTH}
          title={
            depth >= MAX_DEPTH
              ? `Maximum nesting depth (${MAX_DEPTH}) reached`
              : undefined
          }
        >
          <Plus className="h-4 w-4 mr-1" /> Add group
        </Button>
      </div>
    </div>
  );
}
